import { FC, useCallback, useEffect, useMemo } from 'react';
import { FieldValues, SubmitHandler, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Navigate, useNavigate } from 'react-router';
import moment from 'moment';

import { loginRedirect } from 'services/authService';
import { localizationSelector } from 'store/localizationState';
import {
  completeProductRegistrationCancel,
  completeProductRegistrationStart,
  resetProductRegistrationState,
  setCustomerEmail,
  setProductRegistrationInfo,
  setSoldDate
} from 'store/productRegistrationState';
import { authSelector } from 'store/authState';
import { useGetAccountStatusByEmailMutation } from 'store/customerService';
import * as routes from 'router/routes';

import { useQuery } from 'hooks/useQuery';
import { useTranslations } from 'hooks/useTranslations';
import { useAppDispatch, useAppSelector } from 'hooks/redux';
import { useGetProductRegistrationInfoQuery } from 'store/productService';
import { productRegistrationSelector } from 'store/productRegistrationState';
import { customerSelector } from 'store/customerState';
import { findCustomerEmail } from 'utils/customerUtils';

import Loader from 'components/UI/Loader';
import SeoHelmet from 'components/fragments/SeoHelmet';
import Heading, { Type as HeadingType } from 'components/UI/Heading';
import Button from 'components/UI/Buttons/Button';
import InputField from 'components/UI/InputField';
import PageWrapper from 'components/UI/Page/PageWrapper';
import BackButton from 'components/UI/Buttons/BackButton';
import ProductImage from 'components/UI/Product/ProductImage';
import DataList from 'components/UI/DataList';
import Copyright from 'components/UI/Copyright';

import {
  Form,
  BackgroundContainer,
  PageContainer,
  Description,
  Type,
  Section,
  ReceiptNotice
} from './styled';
import ExpandableSection from 'components/UI/ExpandableSection';
import { InfoBox } from 'components/UI/InfoBox';

const ProductInformationPage: FC = () => {
  const t = useTranslations();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  // Redux
  const {
    session: { authenticated }
  } = useAppSelector(authSelector);
  const { productInfo, customerEmail, isCompleted } = useAppSelector(
    productRegistrationSelector
  );
  const { customer } = useAppSelector(customerSelector);
  const { languageCode = 'en' } = useAppSelector(localizationSelector);

  // Zod schema
  const schema = useMemo(
    () =>
      z.object({
        email: z.email('input.email_invalid').min(1, 'input.email_required'),
        soldDate: z
          .string()
          .refine(
            (value) =>
              moment(value).isSameOrBefore(moment().format('YYYY-MM-DD')),
            { message: t('productregistration.date_popup.past') }
          )
          .optional()
      }),
    [t]
  );

  const {
    handleSubmit,
    register,
    formState: { errors },
    resetField,
    setValue
  } = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      email:
        findCustomerEmail(customer?.attributes.eContact) ?? ''.replace('-', '')
    }
  });

  const setDate = useCallback(
    (date: Date) => {
      setValue('soldDate', moment(date).format('YYYY-MM-DD'), {
        shouldDirty: true
      });
    },
    [setValue]
  );

  // Query
  const [pnc, serialNumber, husqvarnaId] = useQuery(
    'productno',
    'serialNumber',
    'husqvarnaId'
  );

  // Hooks
  const { isLoading, isError, data } = useGetProductRegistrationInfoQuery(
    {
      pnc,
      serialNumber,
      husqvarnaId,
      languageCode
    },
    {
      skip: !pnc && !serialNumber && !husqvarnaId
    }
  );
  const [checkAccountStatus, accountStatus] =
    useGetAccountStatusByEmailMutation();

  // Complete
  useEffect(() => {
    if (isCompleted && !productInfo) {
      navigate(routes.PRODUCTS);
    }
  }, [navigate, isCompleted, productInfo]);

  // Found product
  useEffect(() => {
    if (data) {
      dispatch(setProductRegistrationInfo(data));
    }
  }, [dispatch, data]);

  // Errors
  useEffect(() => {
    if (isError) {
      navigate(routes.PRODUCT_REG);
      dispatch(resetProductRegistrationState());
    }
  }, [dispatch, navigate, isError]);

  // Redirect to login or signup
  useEffect(() => {
    const { isSuccess, data } = accountStatus;

    // Redirect when account status has been checked
    if (!isSuccess || !customerEmail) {
      return;
    }

    if (data.loginAccountExists) {
      loginRedirect(languageCode, customerEmail, true);
    } else {
      navigate({
        pathname: routes.SIGNUP,
        search: `?email=${encodeURIComponent(customerEmail)}`
      });
    }
  }, [accountStatus, languageCode, customerEmail, dispatch, navigate]);

  // Update email field
  useEffect(() => {
    if (customerEmail) {
      setValue('email', customerEmail);
    }
  }, [customerEmail, setValue]);

  // Reset soldDate when productInfo changes
  useEffect(() => {
    if (productInfo?.soldDate) {
      resetField('soldDate', {
        defaultValue: moment(productInfo.soldDate).format('YYYY-MM-DD')
      });
    }
  }, [productInfo, resetField]);

  // On register submit
  const onSubmit: SubmitHandler<FieldValues> = useCallback(
    (values) => {
      const unixTimestamp = moment.utc(values.soldDate).valueOf();
      dispatch(setSoldDate(unixTimestamp));

      dispatch(completeProductRegistrationStart());

      if (authenticated) {
        navigate(routes.INVITE_PRODUCTS_REG);
      } else {
        dispatch(setCustomerEmail(values.email));
        checkAccountStatus(values.email);
      }
    },
    [dispatch, navigate, checkAccountStatus, authenticated]
  );

  // Render email input
  const emailInputField = useMemo(() => {
    if (authenticated && customer) {
      return (
        <InputField
          name="email"
          description="input.email_label"
          defaultValue={findCustomerEmail(customer?.attributes.eContact) ?? ''}
          disabled
        />
      );
    }
    return (
      <InputField
        name="email"
        description="input.email_label"
        error={errors.email}
        register={register('email')}
        reset={resetField}
      />
    );
  }, [register, resetField, authenticated, customer, errors]);

  // Render soldDate input
  const soldDateInputField = useMemo(() => {
    if (!authenticated) {
      return null;
    }

    const soldDate = productInfo?.soldDate
      ? moment(productInfo.soldDate).toDate()
      : moment().toDate();

    return (
      <div>
        <InputField
          type="date"
          outlined
          name="soldDate"
          description="productregistration.date_popup.purchase_date"
          defaultValue={moment(soldDate).format('YYYY-MM-DD')}
          maxDate={moment().toDate()}
          defaultDate={soldDate}
          setDate={setDate}
          error={errors.soldDate}
          disabled={!!productInfo?.soldDate}
          register={register('soldDate', {
            required: {
              value: false,
              message: 'productregistration.date_popup.required'
            },
            validate: (value) => {
              return (
                moment(value).isSameOrBefore(moment().format('YYYY-MM-DD')) ||
                t('productregistration.date_popup.past')
              );
            }
          })}
        />
        <ReceiptNotice>
          {t('productregistration.date_popup.receipt_notice')}
        </ReceiptNotice>
      </div>
    );
  }, [register, productInfo, setDate, t, authenticated, errors]);

  // Clear localStorage on back-button click
  const onClick = useCallback(() => {
    dispatch(completeProductRegistrationCancel());
    navigate(routes.PRODUCT_REG);
  }, [dispatch, navigate]);

  // Empty query params
  if (!pnc && !serialNumber && !husqvarnaId) {
    return <Navigate to={routes.PRODUCT_REG} />;
  }

  // Loading
  if (isLoading || !data) {
    return (
      <PageWrapper>
        <SeoHelmet title="productinfo.meta_title" />
        <Loader center size="large" />
      </PageWrapper>
    );
  }

  // One product
  return (
    <BackgroundContainer>
      <SeoHelmet title="productinfo.meta_title" />
      <PageContainer>
        <BackButton onClick={onClick}>{t('productinfo.btn_back')}</BackButton>
        <ProductImage src={data.imageUrl} />
        <Copyright>
          <Type>{data.productCategory?.toLowerCase() ?? '—'}</Type>
          <Heading>{data.modelName ?? '—'}</Heading>
          {data.description && <Description>{data.description}</Description>}
          <Section>
            <ExpandableSection
              title={t('productinfo.section_product_information')}
              openByDefault
              isLast
            >
              <DataList
                data={[
                  {
                    name: t('productinfo.product_number'),
                    value: data.pnc
                  },
                  {
                    name: t('productinfo.serial_number'),
                    value: data.serialNumber
                  },
                  {
                    name: t('productinfo.hid'),
                    value: data.husqvarnaId
                  }
                ]}
              />
            </ExpandableSection>
            {data.pnc !== pnc || data.serialNumber !== serialNumber ? (
              <InfoBox>{t('productinfo.search_returned_top_product')}</InfoBox>
            ) : null}
          </Section>
          <Section>
            <Heading type={HeadingType.h2}>
              {t('productinfo.section_product_registration')}
            </Heading>
            <Form onSubmit={handleSubmit(onSubmit)}>
              {emailInputField}
              {soldDateInputField}
              <Button submit color="success">
                {t(
                  authenticated
                    ? 'productinfo.btn_register_product'
                    : 'productinfo.btn_register_continue'
                )}
              </Button>
            </Form>
          </Section>
        </Copyright>
      </PageContainer>
    </BackgroundContainer>
  );
};

export default ProductInformationPage;
